import { ChangeEvent, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import Link from 'next/link';
import ReCAPTCHA from 'react-google-recaptcha';

import Button from '@core/Button';
import Input from '@core/Input';
import Grid from '@core/layout/Grid';
import Dropdown from '@core/Dropdown';
import Icon from '@core/Icon';
import { fetchKinderopvangLocationsNames } from '@services/location';

import { getInternalPath, IgetInternalPath } from '@helpers/internalLink';
import { notEmpty, isEmail, isPhoneNumber } from '@helpers/string';

const styling = {
  errorMessage: `
    text-body-sm
    text-left
    text-error
  `,
  checkbox: `
    peer
    block
    appearance-none
    cursor-pointer
    rounded-sm
    w-5
    h-5
    min-w-5
    border
    border-shade-300

    checked:bg-primary
    checked:border-primary
    hover:border-primary-300
    hover:bg-primary-50
  `,
  dayButton: `
    px-3
    py-2
    text-body-sm
    rounded-full
    border
    border-shade-300
    cursor-pointer
    transition-colors
    hover:border-primary-300
    hover:bg-primary-50
  `,
  dayButtonActive: `
    bg-primary
    text-white
    border-primary
    hover:bg-primary
    hover:border-primary
  `,
};

interface IInschrijvingForm {
  title: string,
  intro: string,
  emailSubject: string,
  recipientEmail: string,
  privacyLink: {
    externalUrl?: string,
    internalLink?: IgetInternalPath,
  },
  submitButtonLabel: string,
  responseBody: string,
  responseTitle: string,
  locale: string
}

const lookUps = {
  kinderopvang: {
    'Buitenschoolse opvang': 'Buitenschoolse opvang',
    'Peuteropvang': 'Peuteropvang',
    'Kinderdagverblijf': 'Kinderdagverblijf',
  },
};

const productOptions = Object.entries(lookUps.kinderopvang).map(entry => {
  return {
    label: entry[1] as string,
    value: entry[0],
  };
});

const days: Array<{ key: string, label: string }> = [
  { key: 'maandag', label: 'Maandag' },
  { key: 'dinsdag', label: 'Dinsdag' },
  { key: 'woensdag', label: 'Woensdag' },
  { key: 'donderdag', label: 'Donderdag' },
  { key: 'vrijdag', label: 'Vrijdag' },
];

const dayPreferenceOptions: Array<{ key: 'vereist' | 'optioneel' | 'niet-nodig', label: string }> = [
  { key: 'vereist', label: 'Vereist' },
  { key: 'optioneel', label: 'Optioneel' },
  { key: 'niet-nodig', label: 'Niet nodig' },
];

const defaultFieldState = {
  value: '',
  isValid: false,
  validState: {},
  showErrorMessage: false,
};

function slugifyForMatch(input: string): string {
  return (input || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

export default function InschrijvingForm({
  title,
  intro,
  emailSubject,
  recipientEmail,
  privacyLink,
  submitButtonLabel,
  responseBody,
  responseTitle,
}: IInschrijvingForm) {

  const privacyLinkHref = privacyLink?.externalUrl
    ?? (privacyLink?.internalLink ? getInternalPath(privacyLink.internalLink) : undefined);

  const [ formState, setFormState ] = useState<{ [key: string]: any }>({
    childFirstName: { ...defaultFieldState },
    childPreposition: { ...defaultFieldState },
    childLastName: { ...defaultFieldState },
    childNameUnknown: { value: false, isValid: true, showErrorMessage: false },
    birthDate: { ...defaultFieldState },
    startDate: { ...defaultFieldState },
    veIndication: { value: false, isValid: true, showErrorMessage: false },

    cityOfInterest: { ...defaultFieldState },
    productOfInterest: { ...defaultFieldState },
    locationOfInterest: { ...defaultFieldState },
    numberOfDays: { ...defaultFieldState },
    school: { ...defaultFieldState },

    firstName: { ...defaultFieldState },
    initials: { ...defaultFieldState },
    preposition: { ...defaultFieldState },
    lastName: { ...defaultFieldState },
    email: { ...defaultFieldState },
    phone: { ...defaultFieldState },
    parentUnderGuardianship: { value: false, isValid: true, showErrorMessage: false },

    termsAccepted: {
      value: '',
      isValid: false,
      showErrorMessage: false,
    },
    requestTour: { value: false, isValid: true, showErrorMessage: false },
    recaptcha: {
      value: '',
      isValid: false,
      showErrorMessage: false,
    },
  });

  const [ dayPreferences, setDayPreferences ] = useState<{ [day: string]: string }>({});

  const [ loading, setLoading ] = useState(false);
  const [ submitted, setSubmitted ] = useState(false);
  const [ formErrorMessage, setFormErrorMessage ] = useState('');
  const [ locationOptions, setLocationsOptions ] = useState<any[] | null>(null);
  const [ cityOptions, setCityOptions ] = useState<Array<{ label: string, value: string }>>([]);
  const [ cityPrefilled, setCityPrefilled ] = useState(false);

  const responseRef = useRef<HTMLDivElement | null>(null);

  const fetchLocations = useCallback(async() => {
    const result = await fetchKinderopvangLocationsNames(false);

    const newCityOptions = result.reduce((prev, location) => {
      if (Array.isArray(location.products) && location.products.length > 0 && location.products[0].city) {
        if (!Array.isArray(prev)) {
          return [ { label: location.products[0].city.trim(), value: location.products[0].city.trim() } ];
        }
        if (prev.some((option) => option.label === location.products[0].city.trim())) {
          return prev;
        } else {
          prev.push({ label: location.products[0].city.trim(), value: location.products[0].city.trim() });
          return prev;
        }
      } else {
        return prev;
      }
    }, [ ]).sort((a, b) => a.label.localeCompare(b.label));
    setCityOptions(newCityOptions);
    setLocationsOptions(result.map(location => ({ label: location.pageTitle, value: location })));
  }, []);

  useEffect(() => {
    fetchLocations();
  }, []);

  /* Pre-fill plaats o.b.v. URL — match against fetched city names. */
  useEffect(() => {
    if (cityPrefilled || cityOptions.length === 0 || typeof window === 'undefined') {
      return;
    }
    const path = window.location.pathname.toLowerCase();
    const match = cityOptions.find((option) => {
      const slug = slugifyForMatch(option.value);
      if (!slug) {
        return false;
      }
      return path.includes(`/${ slug }`) || path.includes(`-${ slug }`) || path.endsWith(`/${ slug }/`);
    });
    if (match) {
      setFormState((prev) => ({
        ...prev,
        cityOfInterest: {
          ...prev.cityOfInterest,
          value: match,
          isValid: true,
        },
      }));
    }
    setCityPrefilled(true);
  }, [ cityOptions, cityPrefilled ]);

  useEffect(() => {
    if (submitted === true) {
      setTimeout(() => {
        window.scrollTo({ top: (responseRef?.current?.getBoundingClientRect().top || 0) + window.pageYOffset - 270, behavior: 'smooth' });
      }, 50);
    }
  }, [ submitted ]);

  const filteredCityOptions = useMemo(() => {
    return cityOptions.filter((option) => {
      if (
        formState.locationOfInterest &&
        formState.locationOfInterest.value &&
        formState.locationOfInterest.value.products &&
        Array.isArray(formState.locationOfInterest.value.products)
      ) {
        return formState.locationOfInterest.value.products.some(
          (product) => product?.city?.trim() === option.value.trim()
        );
      } else {
        return true;
      }
    });
  }, [ formState, cityOptions ]);

  const filteredLocations = useMemo(() => {
    if (!locationOptions) {
      return [];
    }
    const montrisLocations = locationOptions.filter((location) => location.label.startsWith('Montris'));
    return locationOptions
      .filter((location) => !location.label.startsWith('Montris'))
      .sort((a, b) => a.label.localeCompare(b.label))
      .concat(montrisLocations)
      .filter((location) => {
        if (!formState.productOfInterest.value?.value || formState.productOfInterest.value.value === 'Alle types') {
          return true;
        }
        if (location.value?.products && location.value.products.length > 0) {
          return location.value.products.some(
            (product) => product.productName === formState.productOfInterest.value.value
          );
        }
        return false;
      })
      .filter((location) => {
        if (!formState.cityOfInterest.value?.value || formState.cityOfInterest.value.value === 'Alle plaatsen') {
          return true;
        }
        if (location.value?.products && location.value.products.length > 0) {
          return location.value.products.some(
            (product) => product.city?.trim() === formState.cityOfInterest.value.value.trim()
          );
        }
        return false;
      });
  }, [ locationOptions, formState ]);

  const filteredProductOptions = useMemo(() => {
    return productOptions.filter((option) => {
      if (
        formState.locationOfInterest &&
        formState.locationOfInterest.value &&
        formState.locationOfInterest.value.products &&
        Array.isArray(formState.locationOfInterest.value.products)
      ) {
        return formState.locationOfInterest.value.products.some(
          (product) => product?.productName === option.value
        );
      } else if (
        formState.cityOfInterest &&
        formState.cityOfInterest.value &&
        formState.cityOfInterest.value !== 'Alle plaatsen'
      ) {
        for (const location of filteredLocations) {
          if (location.value.products) {
            if (location.value.products.some((product) => product.productName === option.value)) {
              return true;
            }
          }
        }
        return false;
      }
      return true;
    });
  }, [ formState, filteredLocations ]);

  const isBSO = formState.productOfInterest.value?.value === 'Buitenschoolse opvang';

  const submitInschrijvingForm = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const formElement = (e.target as HTMLFormElement);
    if (loading) {
      return;
    }

    let errorFound = false;

    for (const fieldName in formState) {
      const skipValidation =
        (fieldName === 'childFirstName' && formState.childNameUnknown.value) ||
        (fieldName === 'childLastName') ||
        (fieldName === 'childPreposition') ||
        (fieldName === 'preposition') ||
        (fieldName === 'school' && !isBSO) ||
        (fieldName === 'requestTour') ||
        (fieldName === 'parentUnderGuardianship') ||
        (fieldName === 'veIndication') ||
        (fieldName === 'childNameUnknown');

      if (skipValidation) {
        continue;
      }

      if (!formState[fieldName].isValid) {
        setFormState((prevState: typeof formState) => {
          const nextState = Object.assign({}, prevState);
          nextState[fieldName].showErrorMessage = true;
          return nextState;
        });
        errorFound = true;
      }
    }

    if (errorFound) {
      setTimeout(() => {
        const errorElement: HTMLElement|null = formElement.querySelector('[data-error="true"]');
        if (errorElement) {
          errorElement.focus();
        }
      }, 50);

      return;
    }

    const data = {
      childFirstName: formState.childNameUnknown.value ? '' : formState.childFirstName.value,
      childPreposition: formState.childNameUnknown.value ? '' : formState.childPreposition.value,
      childLastName: formState.childNameUnknown.value ? '' : formState.childLastName.value,
      childNameUnknown: !!formState.childNameUnknown.value,
      birthDate: formState.birthDate.value,
      startDate: formState.startDate.value,
      veIndication: !!formState.veIndication.value,

      cityOfInterest: formState.cityOfInterest.value?.value || '',
      productOfInterest: formState.productOfInterest.value?.value || '',
      locationOfInterest: formState.locationOfInterest.value?.pageTitle ?? formState.locationOfInterest.value,
      locationOfInterestCode: formState.locationOfInterest.value?.locationCode || '',
      numberOfDays: formState.numberOfDays.value,
      dayPreferences: dayPreferences,
      school: isBSO ? formState.school.value : '',

      firstName: formState.firstName.value,
      initials: formState.initials.value,
      preposition: formState.preposition.value,
      lastName: formState.lastName.value,
      email: formState.email.value,
      phone: formState.phone.value,
      parentUnderGuardianship: !!formState.parentUnderGuardianship.value,

      requestTour: !!formState.requestTour.value,

      recipientEmail: recipientEmail,
      emailSubject: emailSubject,
      formLocation: window.location.href,
      recaptcha: formState.recaptcha.value,
    };

    setLoading(true);
    setFormErrorMessage('');

    fetch('/api/inschrijving-form-email', {
      method: 'POST',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((res) => {
        setLoading(false);
        if (res.status === 200 || res.status === 201) {
          setSubmitted(true);
          return;
        }
        return res.text();
      })
      .then(errorMessage => {
        if (errorMessage) {
          setFormErrorMessage('Fout tijdens het verzenden. Probeer het opnieuw!');
          console.error(errorMessage);
        }
      })
      .catch(error => {
        setFormErrorMessage('Fout tijdens het verzenden. Probeer het opnieuw!');
        console.error(error);
      });

  }, [ loading, formState, dayPreferences, isBSO, recipientEmail, emailSubject ]);

  const updateFormState = (
    fieldName: keyof typeof formState,
    key: 'value' | 'isValid' | 'validState' | 'showErrorMessage',
    value: any
  ) => {
    setFormState((prevState: typeof formState) => {
      const nextState = Object.assign({}, prevState);
      nextState[fieldName][key] = value;
      if (key === 'value') {
        nextState[fieldName]['errorMessage'] = '';
      }
      return nextState;
    });
  };

  const handleEmitOnChange = (e: ChangeEvent, fieldName: keyof typeof formState) => {
    updateFormState(fieldName, 'value', (e.target as HTMLInputElement).value);
    updateFormState(fieldName, 'showErrorMessage', false);
  };

  const handleEmitValidate = (validState: { [key: string]: boolean }, fieldName: keyof typeof formState) => {
    let isValid = true;
    for (const validateKey in validState) {
      if (!validState[validateKey]) {
        isValid = false;
        break;
      }
    }
    updateFormState(fieldName, 'isValid', isValid);
    updateFormState(fieldName, 'validState', validState);
  };

  const handleNameUnknownToggle = (checked: boolean) => {
    setFormState((prev) => {
      const next = { ...prev };
      next.childNameUnknown = { ...next.childNameUnknown, value: checked, isValid: true };
      if (checked) {
        next.childFirstName = { ...next.childFirstName, value: '', isValid: true, showErrorMessage: false };
        next.childPreposition = { ...next.childPreposition, value: '', isValid: true, showErrorMessage: false };
        next.childLastName = { ...next.childLastName, value: '', isValid: true, showErrorMessage: false };
      } else {
        next.childFirstName = { ...next.childFirstName, isValid: false };
      }
      return next;
    });
  };

  const handleDayPreferenceClick = (dayKey: string, prefKey: string) => {
    setDayPreferences((prev) => {
      const next = { ...prev };
      if (next[dayKey] === prefKey) {
        delete next[dayKey];
      } else {
        next[dayKey] = prefKey;
      }
      return next;
    });
  };

  const childNameDisabled = !!formState.childNameUnknown.value;

  return (
    <Grid>
      <div className='col-span-8 col-start-3' ref={ responseRef }>
        {
          submitted && !loading &&
          <div id='inschrijving-formulier-succes'>
            <h2 className='text-heading-md md:text-heading-lg mb-4'>{ responseTitle }</h2>
            <div className='text-body-lg md:text-body-xl mb-6 md:mb-8 xl:mb-12'>{ responseBody }</div>
          </div>
        }
        {
          !submitted &&
          <div>
            <h2 className='text-heading-md md:text-heading-lg mb-4'>{ title }</h2>
            <div className='text-body-lg md:text-body-xl mb-6 md:mb-8 xl:mb-12'>{ intro }</div>
          </div>
        }
        { !submitted && (
          <form
            onSubmit={ submitInschrijvingForm }
            className={ loading ? 'opacity-50 pointer-events-none' : '' }
            id='inschrijving-formulier'
          >
            <h3 className='text-heading-xs md:text-heading-sm mb-4'>Gegevens kind</h3>
            <div className='flex flex-col gap-y-4'>
              <div>
                <div className='flex items-center mt-2 relative'>
                  <input
                    type='checkbox'
                    id='childNameUnknown'
                    checked={ !!formState.childNameUnknown.value }
                    className={ styling.checkbox + ' flex-none' }
                    onChange={ (e) => handleNameUnknownToggle(e.target.checked) }
                  />
                  { !!formState.childNameUnknown.value && (
                    <span className='absolute left-[3px] pointer-events-none peer-checked:text-white peer-hover:text-primary'>
                      <Icon name='Done' className='!w-[13.58px]' />
                    </span>
                  ) }
                  <label htmlFor='childNameUnknown' className='pl-4 cursor-pointer select-none'>
                    Ik weet de naam nog niet
                  </label>
                </div>
              </div>
              <Input
                label='Roepnaam'
                required={ !childNameDisabled }
                disabled={ childNameDisabled }
                value={ formState.childFirstName.value }
                isValid={ formState.childFirstName.isValid }
                showErrorMessage={ formState.childFirstName.showErrorMessage }
                emitOnChange={ (e) => handleEmitOnChange(e, 'childFirstName') }
                emitOnValidate={ (e) => handleEmitValidate(e, 'childFirstName') }
                validState={ formState.childFirstName.validState }
                validation={ childNameDisabled ? {} : {
                  required: {
                    errorMessage: 'Vul een roepnaam in (of vink "Ik weet de naam nog niet" aan)',
                  },
                } }
                customStyling={ { container: { className: 'grow' } } }
              />
              <div className='flex-col flex md:flex-row gap-x-4'>
                <Input
                  label='Tussenvoegsel'
                  disabled={ childNameDisabled }
                  value={ formState.childPreposition.value }
                  isValid={ formState.childPreposition.isValid }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'childPreposition') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'childPreposition') }
                  validState={ formState.childPreposition.validState }
                  customStyling={ { container: { className: 'md:max-w-[165px] mb-4 md:mb-0' } } }
                />
                <Input
                  label='Achternaam'
                  disabled={ childNameDisabled }
                  value={ formState.childLastName.value }
                  isValid={ formState.childLastName.isValid }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'childLastName') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'childLastName') }
                  validState={ formState.childLastName.validState }
                  customStyling={ { container: { className: 'grow' } } }
                />
              </div>
              <div className='flex-col flex md:flex-row gap-x-4'>
                <div className='grow mb-4 md:mb-0'>
                  <label htmlFor='birthDate' className='block mb-2'>
                    Geboortedatum (verwacht)
                  </label>
                  <input
                    type='date'
                    id='birthDate'
                    value={ formState.birthDate.value }
                    onChange={ (e) => {
                      updateFormState('birthDate', 'value', e.target.value);
                      updateFormState('birthDate', 'isValid', !!e.target.value);
                      updateFormState('birthDate', 'showErrorMessage', false);
                    } }
                    className='block w-full px-4 py-3 rounded-2xl border border-shade-300 focus:border-primary focus:outline-none'
                    data-error={ formState.birthDate.showErrorMessage ? 'true' : undefined }
                  />
                  { formState.birthDate.showErrorMessage && !formState.birthDate.isValid && (
                    <span className={ styling.errorMessage }>Vul een geboortedatum in</span>
                  ) }
                </div>
                <div className='grow'>
                  <label htmlFor='startDate' className='block mb-2'>
                    Gewenste startdatum
                  </label>
                  <input
                    type='date'
                    id='startDate'
                    value={ formState.startDate.value }
                    onChange={ (e) => {
                      updateFormState('startDate', 'value', e.target.value);
                      updateFormState('startDate', 'isValid', !!e.target.value);
                      updateFormState('startDate', 'showErrorMessage', false);
                    } }
                    className='block w-full px-4 py-3 rounded-2xl border border-shade-300 focus:border-primary focus:outline-none'
                    data-error={ formState.startDate.showErrorMessage ? 'true' : undefined }
                  />
                  { formState.startDate.showErrorMessage && !formState.startDate.isValid && (
                    <span className={ styling.errorMessage }>Vul een gewenste startdatum in</span>
                  ) }
                </div>
              </div>
              <div>
                <div className='flex items-center mt-2 relative'>
                  <input
                    type='checkbox'
                    id='veIndication'
                    checked={ !!formState.veIndication.value }
                    className={ styling.checkbox + ' flex-none' }
                    onChange={ (e) => updateFormState('veIndication', 'value', e.target.checked) }
                  />
                  { !!formState.veIndication.value && (
                    <span className='absolute left-[3px] pointer-events-none peer-checked:text-white peer-hover:text-primary'>
                      <Icon name='Done' className='!w-[13.58px]' />
                    </span>
                  ) }
                  <label htmlFor='veIndication' className='pl-4 cursor-pointer select-none'>
                    Mijn kind heeft een indicatie voor voorschoolse educatie (VE)
                  </label>
                </div>
              </div>

              <h3 className='text-heading-xs md:text-heading-sm mt-2 md:mt-4'>Locatie en opvangdagen</h3>
              <div className='flex-col flex md:flex-row md:flex-wrap gap-4'>
                <Dropdown
                  id='city-of-interest'
                  label='Plaats'
                  isMulti={ false }
                  options={ filteredCityOptions }
                  isSearchable={ true }
                  onChange={ (selected) => {
                    updateFormState('cityOfInterest', 'value', selected);
                    updateFormState('cityOfInterest', 'isValid', selected?.value?.length > 0);
                    updateFormState('cityOfInterest', 'showErrorMessage', !selected || selected?.value?.length === 0);
                  } }
                  placeholder={ '-Kies plaats-' }
                  value={ formState.cityOfInterest.value }
                  errorMessage='Selecteer een plaats'
                  showErrorMessage={ formState.cityOfInterest.showErrorMessage }
                  className='grow z-40 md:whitespace-nowrap min-w-[310px] max-w-[330px]'
                />
                <Dropdown
                  id='product-of-interest'
                  label='Type opvang'
                  isMulti={ false }
                  options={ filteredProductOptions }
                  onChange={ (selected) => {
                    updateFormState('productOfInterest', 'value', selected);
                    updateFormState('productOfInterest', 'isValid', selected?.value?.length > 0);
                    updateFormState('productOfInterest', 'showErrorMessage', !selected || selected?.value?.length === 0);
                  } }
                  placeholder={ '-Kies type opvang-' }
                  value={ formState.productOfInterest.value }
                  errorMessage='Selecteer een type opvang'
                  showErrorMessage={ formState.productOfInterest.showErrorMessage }
                  className='grow z-30 md:whitespace-nowrap min-w-[310px] max-w-[330px]'
                />
                <Dropdown
                  id='location-of-interest'
                  label='Locatie'
                  isSearchable={ true }
                  isMulti={ false }
                  options={ filteredLocations }
                  onChange={ (selected) => {
                    updateFormState('locationOfInterest', 'value', selected?.value);
                    updateFormState('locationOfInterest', 'isValid', selected?.label?.length > 0);
                    updateFormState('locationOfInterest', 'showErrorMessage', !selected || selected?.label?.length === 0);
                  } }
                  placeholder={ '-Kies locatie-' }
                  value={ formState.locationOfInterest.label }
                  errorMessage='Selecteer een locatie'
                  showErrorMessage={ formState.locationOfInterest.showErrorMessage }
                  className='grow z-20 md:whitespace-nowrap min-w-[310px] max-w-[330px]'
                />
              </div>

              <Input
                label='Gewenst aantal dagen'
                required
                value={ formState.numberOfDays.value }
                isValid={ formState.numberOfDays.isValid }
                showErrorMessage={ formState.numberOfDays.showErrorMessage }
                emitOnChange={ (e) => handleEmitOnChange(e, 'numberOfDays') }
                emitOnValidate={ (e) => handleEmitValidate(e, 'numberOfDays') }
                validState={ formState.numberOfDays.validState }
                validation={ {
                  required: {
                    validateFunction: notEmpty,
                    errorMessage: 'Vul het gewenste aantal dagen in',
                  },
                } }
                placeholder='bv: 2'
                customStyling={ { container: { className: 'md:max-w-[200px]' } } }
              />

              <div>
                <label className='block mb-2'>Geef per dag aan of die dag vereist, optioneel of niet nodig is</label>
                <div className='flex flex-col gap-3'>
                  { days.map((day) => (
                    <div key={ day.key } className='flex flex-col md:flex-row md:items-center gap-2 md:gap-4'>
                      <span className='md:w-28'>{ day.label }</span>
                      <div className='flex flex-wrap gap-2'>
                        { dayPreferenceOptions.map((pref) => {
                          const active = dayPreferences[day.key] === pref.key;
                          return (
                            <button
                              key={ pref.key }
                              type='button'
                              onClick={ () => handleDayPreferenceClick(day.key, pref.key) }
                              className={ `${ styling.dayButton } ${ active ? styling.dayButtonActive : '' }` }
                              aria-pressed={ active }
                            >
                              { pref.label }
                            </button>
                          );
                        }) }
                      </div>
                    </div>
                  )) }
                </div>
              </div>

              { isBSO && (
                <Input
                  label='School'
                  required
                  value={ formState.school.value }
                  isValid={ formState.school.isValid }
                  showErrorMessage={ formState.school.showErrorMessage }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'school') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'school') }
                  validState={ formState.school.validState }
                  validation={ {
                    required: {
                      validateFunction: notEmpty,
                      errorMessage: 'Vul een schoolnaam in',
                    },
                  } }
                  customStyling={ { container: { className: 'grow' } } }
                />
              ) }

              <h3 className='text-heading-xs md:text-heading-sm mt-2 md:mt-4'>Gegevens ouder/aanvrager</h3>
              <div className='flex-col flex md:flex-row gap-x-4'>
                <Input
                  label='Voornaam'
                  required
                  value={ formState.firstName.value }
                  isValid={ formState.firstName.isValid }
                  showErrorMessage={ formState.firstName.showErrorMessage }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'firstName') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'firstName') }
                  validState={ formState.firstName.validState }
                  validation={ {
                    required: { errorMessage: 'Je hebt nog geen voornaam ingevuld' },
                  } }
                  customStyling={ { container: { className: 'grow mb-4 md:mb-0' } } }
                />
                <Input
                  label='Voorletters'
                  required
                  value={ formState.initials.value }
                  isValid={ formState.initials.isValid }
                  showErrorMessage={ formState.initials.showErrorMessage }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'initials') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'initials') }
                  validState={ formState.initials.validState }
                  validation={ {
                    required: { errorMessage: 'Vul je voorletters in' },
                  } }
                  customStyling={ { container: { className: 'md:max-w-[165px]' } } }
                />
              </div>
              <div className='flex-col flex md:flex-row gap-x-4'>
                <Input
                  label='Tussenvoegsel'
                  value={ formState.preposition.value }
                  isValid={ formState.preposition.isValid }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'preposition') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'preposition') }
                  validState={ formState.preposition.validState }
                  customStyling={ { container: { className: 'md:max-w-[165px] mb-4 md:mb-0' } } }
                />
                <Input
                  label='Achternaam'
                  required
                  value={ formState.lastName.value }
                  isValid={ formState.lastName.isValid }
                  showErrorMessage={ formState.lastName.showErrorMessage }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'lastName') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'lastName') }
                  validState={ formState.lastName.validState }
                  validation={ {
                    required: { errorMessage: 'Je hebt nog geen achternaam ingevuld' },
                  } }
                  customStyling={ { container: { className: 'grow' } } }
                />
              </div>
              <div className='flex-col flex md:flex-row gap-x-4'>
                <Input
                  label='E-mailadres'
                  required
                  value={ formState.email.value }
                  isValid={ formState.email.isValid }
                  showErrorMessage={ formState.email.showErrorMessage }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'email') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'email') }
                  validState={ formState.email.validState }
                  validation={ {
                    required: {
                      validateFunction: notEmpty,
                      errorMessage: 'Je hebt nog geen e-mailadres ingevuld',
                    },
                    pattern: {
                      validateFunction: isEmail,
                      errorMessage: 'Je hebt een ongeldig e-mailadres ingevuld. Kun je het controleren?',
                    },
                  } }
                  customStyling={ { container: { className: 'grow mb-4 md:mb-0' } } }
                />
                <Input
                  label='Telefoonnummer'
                  required
                  value={ formState.phone.value }
                  isValid={ formState.phone.isValid }
                  showErrorMessage={ formState.phone.showErrorMessage }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'phone') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'phone') }
                  validState={ formState.phone.validState }
                  validation={ {
                    required: {
                      validateFunction: notEmpty,
                      errorMessage: 'Vul een telefoonnummer in',
                    },
                    pattern: {
                      validateFunction: isPhoneNumber,
                      errorMessage: 'Dit is geen geldig telefoonnummer. Kun je het controleren?',
                    },
                  } }
                  placeholder='bv: 0101234567/0612345678'
                  customStyling={ { container: { className: 'grow' } } }
                />
              </div>
              <div>
                <div className='flex items-center mt-2 relative'>
                  <input
                    type='checkbox'
                    id='parentUnderGuardianship'
                    checked={ !!formState.parentUnderGuardianship.value }
                    className={ styling.checkbox + ' flex-none' }
                    onChange={ (e) => updateFormState('parentUnderGuardianship', 'value', e.target.checked) }
                  />
                  { !!formState.parentUnderGuardianship.value && (
                    <span className='absolute left-[3px] pointer-events-none peer-checked:text-white peer-hover:text-primary'>
                      <Icon name='Done' className='!w-[13.58px]' />
                    </span>
                  ) }
                  <label htmlFor='parentUnderGuardianship' className='pl-4 cursor-pointer select-none'>
                    Ik sta momenteel onder curatele of bewind
                  </label>
                </div>
              </div>

              <h3 className='text-heading-xs md:text-heading-sm mt-2 md:mt-4'>Akkoord</h3>
              <div>
                <div className='flex items-center mt-2 relative'>
                  <input
                    type='checkbox'
                    id='termsAccepted'
                    checked={ !!formState.termsAccepted.value }
                    className={ styling.checkbox + ' flex-none' }
                    onChange={ (e) => {
                      updateFormState('termsAccepted', 'value', e.target.checked);
                      updateFormState('termsAccepted', 'isValid', e.target.checked);
                      updateFormState('termsAccepted', 'showErrorMessage', false);
                    } }
                  />
                  { !!formState.termsAccepted.value && (
                    <span className='absolute left-[3px] pointer-events-none peer-checked:text-white peer-hover:text-primary'>
                      <Icon name='Done' className='!w-[13.58px]' />
                    </span>
                  ) }
                  <label htmlFor='termsAccepted' className='pl-4 cursor-pointer select-none'>
                    Ik ga akkoord met het vastleggen van mijn gegevens volgens het{ ' ' }
                    { privacyLinkHref
                      ? <Link
                        href={ privacyLinkHref }
                        className='text-primary underline'
                        target='_blank'>
                        privacybeleid
                      </Link>
                      : 'privacybeleid'
                    }
                  </label>
                </div>
                { !formState.termsAccepted.isValid && formState.termsAccepted.showErrorMessage && (
                  <span className={ styling.errorMessage }>Je hebt nog geen toestemming gegeven</span>
                ) }
              </div>
              <div>
                <div className='flex items-center mt-2 relative'>
                  <input
                    type='checkbox'
                    id='requestTour'
                    checked={ !!formState.requestTour.value }
                    className={ styling.checkbox + ' flex-none' }
                    onChange={ (e) => updateFormState('requestTour', 'value', e.target.checked) }
                  />
                  { !!formState.requestTour.value && (
                    <span className='absolute left-[3px] pointer-events-none peer-checked:text-white peer-hover:text-primary'>
                      <Icon name='Done' className='!w-[13.58px]' />
                    </span>
                  ) }
                  <label htmlFor='requestTour' className='pl-4 cursor-pointer select-none'>
                    Ik zou graag een rondleiding plannen
                  </label>
                </div>
              </div>

              <div className='relative z-10 mt-4 mb-2 md:mb-4 xl:mb-8 max-w-[305px]'>
                <ReCAPTCHA
                  sitekey={ process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY }
                  onChange={ (captchaCode) => {
                    if (!captchaCode) {
                      updateFormState('recaptcha', 'value', false);
                      updateFormState('recaptcha', 'isValid', false);
                      return;
                    }
                    updateFormState('recaptcha', 'value', captchaCode);
                    updateFormState('recaptcha', 'isValid', true);
                  } }
                  onExpired={ () => {
                    updateFormState('recaptcha', 'value', false);
                    updateFormState('recaptcha', 'isValid', false);
                  } }
                />
                { !formState.recaptcha.isValid && formState.recaptcha.showErrorMessage && (
                  <span className={ styling.errorMessage }>Controleer of je geen robot bent.</span>
                ) }
              </div>
              <div>
                <Button disabled={ loading }>
                  <span>{ submitButtonLabel }</span>
                </Button>
                { formErrorMessage && (
                  <span className={ `${ styling.errorMessage } md:w-1/2 ml-2` }>Fout tijdens het verzenden. Probeer het opnieuw!</span>
                ) }
              </div>
            </div>
          </form>
        ) }
      </div>
    </Grid>
  );
}
